import { useCurrentTenant, useCurrentUser } from '_graphql/cache/auth';
import { useTenantBranding } from 'providers/tenant-branding-provider';

interface AppInfo {
  name: string;
  version: string;
  logo: string;
  url: string;
  country: string | undefined;
  host: string;
  isReady: boolean;
  theme?: 'light' | 'dark';
}

/**
 * Hook to get application info based on the current tenant
 *
 * Returns tenant-specific branding when available:
 * - name: Tenant organization name
 * - logo: Tenant logo URL (preloaded)
 * - isReady: Whether branding has finished loading
 *
 * Falls back to default values when no tenant is set.
 */
const useApp = (): AppInfo => {
  const currentUser = useCurrentUser();
  const currentTenant = useCurrentTenant();
  const branding = useTenantBranding();

  const host = typeof window !== 'undefined' ? window.location.hostname : '';

  // Use tenant branding if available
  const name = currentTenant?.name || 'Tendo';
  const logo =
    branding.logo || 'https://tendo.app/wp-content/uploads/2023/07/logo-2.jpeg';

  // Build tenant URL
  const tenantUrl = currentTenant?.slug
    ? `https://${currentTenant.slug}.tendo.one`
    : 'https://tendo.one';

  return {
    name,
    version: '1.0.0',
    logo,
    url: tenantUrl,
    country: currentUser?.supplierSignIn?.supplier?.country ?? undefined,
    host,
    isReady: branding.isReady,
  };
};

export default useApp;
